import React, { useState } from 'react';
import { Link } from '../Link';
import { useRouter } from '../Router';
import { Logo } from '../Logo';
import SEO from '../SEO';
import {
    ArrowRight, CheckCircle2, ShieldCheck, AlertTriangle, Copy, Check,
    Calculator, Sparkles, BookOpen, Clock, HelpCircle, ChevronDown,
    ChevronUp, Database, Bot, CreditCard, Terminal, Zap, Globe
} from 'lucide-react';

export type NigeriaPlaybookSlug =
    | 'whatsapp-api-pricing-nigeria'
    | 'wasender-gbwhatsapp-ban-official-meta-api-nigeria'
    | 'paystack-whatsapp-order-automation-google-sheets-nigeria';

interface PlaybookConfig {
    slug: NigeriaPlaybookSlug;
    title: string;
    seoTitle: string;
    seoDescription: string;
    keywords: string;
    category: string;
    readTime: string;
    publishedDate: string;
    publishedIso: string;
    badge: string;
    subtitle: string;
    keyMetrics: Array<{ value: string; label: string; sub: string }>;
    toc: Array<{ id: string; label: string }>;
    faqs: Array<{ question: string; answer: string }>;
}

const PLAYBOOKS: Record<NigeriaPlaybookSlug, PlaybookConfig> = {
    'whatsapp-api-pricing-nigeria': {
        slug: 'whatsapp-api-pricing-nigeria',
        title: 'Official Meta WhatsApp API Pricing in Nigeria (2026 Naira Breakdown vs. Wati & Twilio)',
        seoTitle: 'WhatsApp API Pricing in Nigeria (2026 Naira Guide vs Wati & Twilio)',
        seoDescription: 'Complete 2026 breakdown of official Meta WhatsApp Cloud API pricing in Nigeria. Compare Naira (₦) subscription costs across Bytecohort Reach, Wati, and Twilio with our interactive NGN calculator.',
        keywords: 'WhatsApp API pricing Nigeria, Meta WhatsApp Cloud API cost Naira, Wati pricing Nigeria, Twilio WhatsApp Nigeria cost, Pay in Naira WhatsApp automation, Paystack WhatsApp API billing',
        category: 'Nigeria Growth & Pricing',
        readTime: '8 min read',
        publishedDate: 'Oct 2026',
        publishedIso: '2026-10-10T09:00:00+01:00',
        badge: '2026 Naira Pricing Guide 🇳🇬',
        subtitle: 'Stop paying unpredictable USD card markups and per-message broker fees. Understand how Meta WhatsApp Cloud API conversation categories work in Nigeria and how to pay in Naira (₦) via Paystack.',
        keyMetrics: [
            { value: '₦0', label: 'Service Reply Window Fee', sub: 'First 1,000 user-initiated service chats/mo are free on Meta' },
            { value: '100% NGN', label: 'Local Naira Billing', sub: 'Pay via Paystack, Verve, Mastercard, or Bank Transfer' },
            { value: '0% Markup', label: 'Direct Meta Cloud API', sub: 'No middleman per-message broker surcharges' }
        ],
        toc: [
            { id: 'naira-calculator', label: '1. Interactive Naira WhatsApp Cost & ROI Calculator' },
            { id: 'meta-categories', label: '2. How Meta Conversation Categories Work in Nigeria' },
            { id: 'platform-comparison', label: '3. Bytecohort Reach vs. Wati vs. Twilio in Nigeria' },
            { id: 'avoid-fx-traps', label: '4. Eliminating USD Virtual Card Blocks & FX Volatility' },
            { id: 'faq', label: '5. Frequently Asked Questions (Nigeria Billing)' }
        ],
        faqs: [
            {
                question: 'How much does the official WhatsApp Business API cost in Nigeria?',
                answer: 'With Bytecohort Reach, Nigerian businesses can start on the Free Sandbox tier (₦0) and upgrade to Naira plans starting at ₦29,000/month via Paystack. Meta also provides 1,000 free customer-initiated service conversations every month per WhatsApp Business Account.'
            },
            {
                question: 'Why are Wati and Twilio more expensive for Nigerian businesses?',
                answer: 'Platforms like Wati and Twilio bill strictly in USD ($49–$119+/month), charge additional per-message broker markups on top of Meta rates, and require international USD cards that frequently fail with Nigerian banking restrictions. Bytecohort Reach bills natively in Naira (₦) with zero per-message broker markup.'
            },
            {
                question: 'Are customer service replies on WhatsApp free in Nigeria?',
                answer: 'Yes! Under Meta Cloud API rules, whenever a Nigerian customer messages your business first, a 24-hour customer service window opens. All replies sent by your Gemini AI chatbot or human support team within that 24-hour window incur zero Meta template charges.'
            },
            {
                question: 'Can I pay with a standard Nigerian Naira debit card or bank transfer?',
                answer: 'Yes. Bytecohort Reach integrates directly with Paystack, allowing instant payment via Nigerian Verve, Mastercard, Visa, USSD, or direct corporate bank transfer.'
            }
        ]
    },
    'wasender-gbwhatsapp-ban-official-meta-api-nigeria': {
        slug: 'wasender-gbwhatsapp-ban-official-meta-api-nigeria',
        title: 'Why Wasender & GBWhatsApp Get +234 Numbers Banned in Nigeria (And How to Migrate to Official Meta API)',
        seoTitle: 'Stop +234 WhatsApp Number Bans: Wasender Alternative in Nigeria (Official Meta API)',
        seoDescription: 'Discover why unofficial bulk senders like Wasender, GBWhatsApp, and Chrome extensions get Nigerian +234 numbers permanently banned, and how to migrate to the 100% anti-ban official Meta Cloud API in 5 minutes.',
        keywords: 'WhatsApp number banned Nigeria, Wasender alternative Nigeria, Official Bulk WhatsApp Sender Nigeria, Anti-ban WhatsApp marketing Nigeria, GBWhatsApp business ban +234, Meta Cloud API Nigeria setup',
        category: 'Compliance & Anti-Ban',
        readTime: '7 min read',
        publishedDate: 'Oct 2026',
        publishedIso: '2026-10-14T09:00:00+01:00',
        badge: 'Anti-Ban Security Playbook 🛡️',
        subtitle: 'Losing your primary +234 business line overnight destroys months of customer trust and active orders. Learn how Meta detects browser automation scripts and how to switch to verified Meta Graph API v21.0 infrastructure.',
        keyMetrics: [
            { value: '0.00%', label: 'Ban Rate on Official API', sub: 'Verified Meta Graph API v21.0 template dispatch' },
            { value: '80+ msg/s', label: 'Authorized Throughput', sub: 'No slow browser tab simulation or DOM scraping' },
            { value: '5 Mins', label: '1-Click Meta Migration', sub: 'Embedded signup keeps your existing +234 number' }
        ],
        toc: [
            { id: 'ban-risk-checker', label: '1. Interactive +234 Number Ban-Risk Diagnostic' },
            { id: 'how-meta-detects', label: '2. How Meta Detects Wasender, Chrome Extensions & Modified APKs' },
            { id: 'architecture-diff', label: '3. Unofficial DOM Scrapers vs. Official Meta Cloud API' },
            { id: 'migration-steps', label: '4. Step-by-Step: Migrating Your +234 Number in 5 Minutes' },
            { id: 'faq', label: '5. Frequently Asked Questions (+234 Number Safety)' }
        ],
        faqs: [
            {
                question: 'Why did WhatsApp ban my Nigerian +234 business number after sending bulk messages?',
                answer: 'WhatsApp uses automated device fingerprinting and behavioral velocity detection. Tools like Wasender, WA Bulk Sender Chrome extensions, and GBWhatsApp simulate rapid human clicks inside WhatsApp Web without approved templates. Meta flags this within minutes and issues a permanent or 72-hour ban on the +234 SIM.'
            },
            {
                question: 'How can I send bulk WhatsApp messages in Nigeria without getting banned?',
                answer: 'The only 100% anti-ban method is using the Official Meta WhatsApp Cloud API through an authorized platform like Bytecohort Reach. You dispatch pre-approved Marketing or Utility templates directly through Meta servers rather than automating a phone or browser.'
            },
            {
                question: 'Can I keep my existing Nigerian (+234) phone number when moving to Bytecohort Reach?',
                answer: 'Yes. Using Meta Embedded Signup inside Bytecohort Reach, you can link your existing Nigerian MTN, Airtel, Glo, or 9mobile number. You simply remove it from the consumer WhatsApp app first so it can be registered on the Cloud API.'
            },
            {
                question: 'What happens if my customers are on MTN or Airtel DND (Do-Not-Disturb)?',
                answer: 'Unlike bulk SMS in Nigeria—where up to 40% of messages fail due to NCC DND restrictions—WhatsApp Cloud API messages deliver over data/internet with 99.8% reliability across MTN, Airtel, Glo, and 9mobile.'
            }
        ]
    },
    'paystack-whatsapp-order-automation-google-sheets-nigeria': {
        slug: 'paystack-whatsapp-order-automation-google-sheets-nigeria',
        title: 'How to Automate WhatsApp Order Confirmations, Paystack Receipts & Delivery Alerts with Google Sheets in Nigeria',
        seoTitle: 'Automate WhatsApp Orders, Paystack & Google Sheets in Nigeria (2026 Blueprint)',
        seoDescription: 'Step-by-step blueprint for Nigerian e-commerce stores, logistics companies, and real estate teams to automate WhatsApp order confirmations, Paystack payment links, and 24/7 Gemini AI support using Google Sheets.',
        keywords: 'Automate WhatsApp orders Nigeria, Paystack WhatsApp automation, Google Sheets WhatsApp Nigeria, WhatsApp chatbot for e-commerce Lagos, WhatsApp delivery notification Nigeria, Nigerian AI customer support bot',
        category: 'E-Commerce & Workflows',
        readTime: '9 min read',
        publishedDate: 'Oct 2026',
        publishedIso: '2026-10-18T09:00:00+01:00',
        badge: 'E-Commerce & Logistics Blueprint ⚡',
        subtitle: 'Stop saying "DM for price" and manually checking bank alerts. Build an automated pipeline that normalizes Nigerian phone numbers (080/081/+234), sends instant WhatsApp order updates from Google Sheets, and closes sales 24/7 with Gemini AI.',
        keyMetrics: [
            { value: '< 280ms', label: 'Sheet-to-WhatsApp Sync', sub: 'Instant dispatch when an order row updates' },
            { value: '24/7 AI', label: 'Nigerian Context Agent', sub: 'Answers pricing, delivery fees & stock in real time' },
            { value: '+234 Auto', label: 'Smart Number Sanitizer', sub: 'Converts 0803, 0812, 0905, 0701 to +234 E.164 automatically' }
        ],
        toc: [
            { id: 'copyable-templates', label: '1. Copyable Nigerian WhatsApp Templates & AI Prompts' },
            { id: 'sheets-pipeline', label: '2. Designing Your Google Sheets Order & Logistics Pipeline' },
            { id: 'number-normalization', label: '3. Automatic +234 Phone Number Normalization (080, 081, 090)' },
            { id: 'ai-handoff', label: '4. Training Gemini AI for Lagos/Abuja Delivery & Human Handoff' },
            { id: 'faq', label: '5. Frequently Asked Questions (Order Automation)' }
        ],
        faqs: [
            {
                question: 'Do I need a developer to connect Google Sheets to WhatsApp in Nigeria?',
                answer: 'No. Bytecohort Reach features a built-in 1-click Google Sheets integration. You simply select your spreadsheet, map your phone number and customer name columns, and turn on recurring sync or real-time AI lookup.'
            },
            {
                question: 'What if my Nigerian customers enter phone numbers starting with 080 or 081 instead of +234?',
                answer: 'Bytecohort Reach automatically normalizes local Nigerian phone formats. Whether a customer types 08031234567, 2348031234567, or +234 803 123 4567 into your checkout form or Google Sheet, the engine formats it cleanly before sending.'
            },
            {
                question: 'Can the WhatsApp AI chatbot check live order status from my Google Sheet?',
                answer: 'Yes! Using the Google Sheets Tool inside Bytecohort Reach Agent Builder, when a customer asks "Where is my order?" on WhatsApp, the Gemini AI agent looks up their +234 phone number or Order ID in your Google Sheet in under 280ms and replies with their live rider/waybill status.'
            },
            {
                question: 'Can a human sales rep take over the WhatsApp chat when a VIP buyer is ready to pay?',
                answer: 'Yes. Whenever the AI detects high purchase intent or a custom bulk request, it triggers the Human Handoff node, pauses automated replies, and alerts your sales team inside the Shared Team Inbox.'
            }
        ]
    }
};

export const NigeriaPlaybookPage: React.FC<{ slug: NigeriaPlaybookSlug }> = ({ slug }) => {
    const { navigate } = useRouter();
    const config = PLAYBOOKS[slug] || PLAYBOOKS['whatsapp-api-pricing-nigeria'];

    // State for interactive calculators & copyable blocks
    const [monthlyContacts, setMonthlyContacts] = useState<number>(3000);
    const [avgOrderValueNgn, setAvgOrderValueNgn] = useState<number>(25000);
    const [openFaq, setOpenFaq] = useState<number | null>(0);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    // State for Ban-Risk Diagnostic (Playbook 2)
    const [usesBrowserExtension, setUsesBrowserExtension] = useState<boolean>(true);
    const [sendsColdUnapproved, setSendsColdUnapproved] = useState<boolean>(true);
    const [noOptOutButton, setNoOptOutButton] = useState<boolean>(false);

    const handleCopy = (key: string, text: string) => {
        navigator.clipboard.writeText(text);
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2000);
    };

    // ROI Calculations for Playbook 1
    const reachMonthlyNgn = monthlyContacts <= 2500 ? 29000 : 75000;
    const competitorUsd = monthlyContacts <= 2500 ? 79 : 159;
    const competitorNgnEquivalent = competitorUsd * 1650; // Realistic FX + card markup
    const monthlyNgnSavings = Math.max(0, competitorNgnEquivalent - reachMonthlyNgn);
    const projectedConversions = Math.round(monthlyContacts * 0.045);
    const projectedRevenueNgn = projectedConversions * avgOrderValueNgn;

    // Ban Risk Score for Playbook 2
    const banRiskScore = (usesBrowserExtension ? 55 : 0) + (sendsColdUnapproved ? 30 : 0) + (noOptOutButton ? 15 : 0);

    const COPY_SNIPPETS = {
        metaTemplate: `Hello {{1}} 👋, your order *#{{2}}* has been confirmed!

📦 *Items:* {{3}}
💳 *Payment Status:* Paid (₦{{4}} via Paystack)
🚚 *Estimated Delivery:* {{5}}

Reply *TRACK* anytime to check your live dispatch rider status, or reply *AGENT* to speak with our Lagos support desk.`,
        geminiPrompt: `You are Tolu, the official AI Sales & Order Support Assistant for our Nigerian business on WhatsApp.

RULES & NIGERIAN CONTEXT:
1. Greet customers warmly. Understand both standard English and common Nigerian conversational phrases (e.g., "How much last?", "Abeg when will my rider arrive?", "Send account details").
2. When a customer asks about an existing order or delivery status, immediately run the [Google_Sheets_Order_Lookup] tool using their +234 phone number.
3. For Lagos Mainland/Island deliveries, standard dispatch is 24 hours (₦3,500–₦5,000). For Abuja, Port Harcourt, and interstate GIG/DHL waybills, delivery is 2–3 working days.
4. Never invent prices or stock levels. If a customer wants to buy in bulk or requests a custom bank transfer invoice, trigger [Human_Inbox_Handoff] immediately.`
    };

    return (
        <div className="min-h-screen bg-[#07080B] text-zinc-100 font-sans antialiased selection:bg-emerald-500/30 selection:text-emerald-200">
            <SEO
                title={config.seoTitle}
                description={config.seoDescription}
                keywords={config.keywords}
                canonical={`https://bytecohortreach.com/blog/${config.slug}`}
                type="article"
                publishedTime={config.publishedIso}
                category={config.category}
                faqs={config.faqs}
            />

            {/* Top Status Strip */}
            <div className="w-full bg-[#0B0D14] border-b border-white/[0.06] py-1.5 px-4 text-[11px] font-mono text-zinc-400">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-emerald-400">Nigeria WhatsApp Automation Playbook</span>
                        <span className="hidden sm:inline text-zinc-600">•</span>
                        <span className="hidden sm:inline">Official Meta Cloud API v21.0</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link href="/whatsapp-automation-nigeria" className="text-emerald-400 hover:underline">
                            Nigeria Pillar Hub 🇳🇬
                        </Link>
                    </div>
                </div>
            </div>

            {/* Sticky Header */}
            <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#07080B]/85 border-b border-white/[0.08]">
                <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-8">
                        <Link href="/" className="flex items-center space-x-2">
                            <Logo className="h-7 w-auto" />
                        </Link>
                        <div className="hidden md:flex items-center gap-6 text-xs font-medium text-zinc-400">
                            <Link href="/whatsapp-automation-nigeria" className="text-emerald-400 hover:text-emerald-300 transition-colors font-semibold">
                                Nigeria Hub 🇳🇬
                            </Link>
                            <Link href="/blog" className="text-white hover:text-emerald-400 transition-colors">
                                All Playbooks
                            </Link>
                            <Link href="/pricing" className="hover:text-white transition-colors">
                                Naira Pricing
                            </Link>
                            <Link href="/docs" className="hover:text-white transition-colors">
                                Docs
                            </Link>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link
                            href="/app"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-black transition-all shadow-lg shadow-emerald-500/20"
                        >
                            <span>Start Free in Naira (₦)</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                </nav>
            </header>

            {/* Breadcrumb Bar */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 text-xs font-mono text-zinc-400 flex flex-wrap items-center gap-2">
                <Link href="/" className="hover:text-white">Home</Link>
                <span>/</span>
                <Link href="/whatsapp-automation-nigeria" className="text-emerald-400 hover:underline">WhatsApp Automation Nigeria</Link>
                <span>/</span>
                <Link href="/blog" className="hover:text-white">Playbooks</Link>
                <span>/</span>
                <span className="text-zinc-300 truncate max-w-xs sm:max-w-md">{config.title}</span>
            </div>

            {/* Article Hero */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-12 border-b border-white/[0.06]">
                <div className="max-w-4xl">
                    <div className="flex flex-wrap items-center gap-3 mb-5">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono">
                            <Sparkles className="w-3.5 h-3.5" />
                            {config.badge}
                        </span>
                        <span className="text-xs font-mono text-zinc-400 flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-emerald-400" />
                            {config.readTime}
                        </span>
                        <span className="text-xs font-mono text-zinc-500">
                            Updated {config.publishedDate}
                        </span>
                    </div>

                    <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-[1.15] mb-6">
                        {config.title}
                    </h1>

                    <p className="text-base sm:text-lg text-zinc-300 leading-relaxed mb-8">
                        {config.subtitle}
                    </p>

                    {/* Key Metrics Strip */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {config.keyMetrics.map((m, i) => (
                            <div key={i} className="p-4 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                <div className="text-2xl font-black text-emerald-400 font-mono mb-1">{m.value}</div>
                                <div className="text-xs font-semibold text-white mb-0.5">{m.label}</div>
                                <div className="text-[11px] text-zinc-400">{m.sub}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Main 2-Column Content Layout */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Left Sticky Sidebar: Table of Contents + Pillar Link */}
                <aside className="lg:col-span-4 space-y-6">
                    <div className="sticky top-24 space-y-6">
                        <div className="p-6 rounded-2xl bg-[#0B0D14] border border-white/[0.08]">
                            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-emerald-400 mb-4">
                                <BookOpen className="w-3.5 h-3.5" />
                                <span>Table of Contents</span>
                            </div>
                            <ul className="space-y-2.5 text-xs text-zinc-300">
                                {config.toc.map((item) => (
                                    <li key={item.id}>
                                        <a
                                            href={`#${item.id}`}
                                            className="hover:text-emerald-400 transition-colors block py-1 border-l-2 border-white/10 hover:border-emerald-400 pl-3"
                                        >
                                            {item.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Hub & Spoke Internal Link Card */}
                        <div className="p-6 rounded-2xl bg-gradient-to-b from-emerald-950/30 to-[#0B0D14] border border-emerald-500/30">
                            <span className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                Nigeria Pillar Guide
                            </span>
                            <h3 className="text-base font-bold text-white mt-3 mb-2">
                                Complete WhatsApp Automation Guide for Nigeria
                            </h3>
                            <p className="text-xs text-zinc-300 leading-relaxed mb-4">
                                Explore our master guide covering official Meta Cloud API onboarding, Paystack Naira billing, and 24/7 Gemini AI agents.
                            </p>
                            <Link
                                href="/whatsapp-automation-nigeria"
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-xs transition-all"
                            >
                                <span>Explore Nigeria Hub</span>
                                <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                    </div>
                </aside>

                {/* Right Column: Deep-Dive Article & Interactive Tools */}
                <article className="lg:col-span-8 space-y-12">
                    {/* PLAYBOOK 1 SPECIFIC CONTENT: PRICING & NAIRA CALCULATOR */}
                    {slug === 'whatsapp-api-pricing-nigeria' && (
                        <>
                            <section id="naira-calculator" className="p-6 sm:p-8 rounded-2xl bg-[#0B0D14] border border-emerald-500/30 shadow-xl">
                                <div className="flex items-center gap-2 text-emerald-400 text-xs font-mono uppercase tracking-wider mb-2">
                                    <Calculator className="w-4 h-4" />
                                    <span>Interactive Naira (₦) Cost & Savings Estimator</span>
                                </div>
                                <h2 className="text-2xl font-bold text-white mb-2">
                                    1. Calculate Your Monthly WhatsApp Automation Cost & ROI in Naira
                                </h2>
                                <p className="text-xs sm:text-sm text-zinc-400 mb-6">
                                    Adjust your monthly Nigerian customer contacts and average order value below to compare local Naira billing on Bytecohort Reach against USD-billed platforms like Wati or Twilio.
                                </p>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                                    <div className="p-4 rounded-xl bg-zinc-900/70 border border-white/[0.06]">
                                        <div className="flex justify-between text-xs mb-2">
                                            <span className="text-zinc-300 font-medium">Monthly Active Contacts:</span>
                                            <span className="text-emerald-400 font-mono font-bold">{monthlyContacts.toLocaleString()} contacts</span>
                                        </div>
                                        <input
                                            type="range"
                                            min={500}
                                            max={20000}
                                            step={500}
                                            value={monthlyContacts}
                                            onChange={(e) => setMonthlyContacts(Number(e.target.value))}
                                            className="w-full accent-emerald-400 cursor-pointer"
                                        />
                                        <div className="flex justify-between text-[10px] font-mono text-zinc-500 mt-1">
                                            <span>500</span>
                                            <span>10,000</span>
                                            <span>20,000</span>
                                        </div>
                                    </div>

                                    <div className="p-4 rounded-xl bg-zinc-900/70 border border-white/[0.06]">
                                        <div className="flex justify-between text-xs mb-2">
                                            <span className="text-zinc-300 font-medium">Average Order Value (₦):</span>
                                            <span className="text-emerald-400 font-mono font-bold">₦{avgOrderValueNgn.toLocaleString()}</span>
                                        </div>
                                        <input
                                            type="range"
                                            min={5000}
                                            max={200000}
                                            step={5000}
                                            value={avgOrderValueNgn}
                                            onChange={(e) => setAvgOrderValueNgn(Number(e.target.value))}
                                            className="w-full accent-emerald-400 cursor-pointer"
                                        />
                                        <div className="flex justify-between text-[10px] font-mono text-zinc-500 mt-1">
                                            <span>₦5,000</span>
                                            <span>₦100,000</span>
                                            <span>₦200,000</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                                        <div className="text-[11px] font-mono text-emerald-300 uppercase">Bytecohort Reach (Naira)</div>
                                        <div className="text-2xl font-black text-white font-mono mt-1">₦{reachMonthlyNgn.toLocaleString()}/mo</div>
                                        <div className="text-[11px] text-zinc-300 mt-1">Pay via Paystack / Bank Transfer</div>
                                    </div>
                                    <div className="p-4 rounded-xl bg-zinc-900/80 border border-white/[0.08]">
                                        <div className="text-[11px] font-mono text-zinc-400 uppercase">Annual FX & Fee Savings</div>
                                        <div className="text-2xl font-black text-emerald-400 font-mono mt-1">₦{(monthlyNgnSavings * 12).toLocaleString()}/yr</div>
                                        <div className="text-[11px] text-zinc-400 mt-1">Saved vs. USD broker platforms</div>
                                    </div>
                                    <div className="p-4 rounded-xl bg-zinc-900/80 border border-white/[0.08]">
                                        <div className="text-[11px] font-mono text-cyan-400 uppercase">Est. Monthly Sales (4.5% Conv.)</div>
                                        <div className="text-2xl font-black text-white font-mono mt-1">₦{projectedRevenueNgn.toLocaleString()}</div>
                                        <div className="text-[11px] text-zinc-400 mt-1">~{projectedConversions} automated WhatsApp orders</div>
                                    </div>
                                </div>
                            </section>

                            <section id="meta-categories" className="space-y-4">
                                <h2 className="text-2xl font-bold text-white">
                                    2. How Meta WhatsApp Conversation Categories Work in Nigeria
                                </h2>
                                <p className="text-sm text-zinc-300 leading-relaxed">
                                    Unlike legacy SMS gateways in Nigeria that charge per 160-character page (and fail on DND numbers), the <strong>Official Meta WhatsApp Cloud API</strong> uses a 24-hour conversation window model divided into four categories:
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="p-5 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                        <div className="text-xs font-mono text-emerald-400 font-bold mb-1">1. Service Conversations (User-Initiated)</div>
                                        <p className="text-xs text-zinc-300 leading-relaxed">
                                            When a customer messages your +234 WhatsApp number from Instagram, your website widget, or a direct chat link, replies from your <strong>Google Gemini AI Agent</strong> or human team within 24 hours are <strong>100% free of Meta template fees</strong>.
                                        </p>
                                    </div>
                                    <div className="p-5 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                        <div className="text-xs font-mono text-cyan-400 font-bold mb-1">2. Utility Templates (Order & Payment Alerts)</div>
                                        <p className="text-xs text-zinc-300 leading-relaxed">
                                            Triggered automatically from Google Sheets or Paystack webhooks to confirm orders, send delivery tracking, or issue payment receipts. Utility templates enjoy ultra-low Meta rates and near-100% open rates.
                                        </p>
                                    </div>
                                    <div className="p-5 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                        <div className="text-xs font-mono text-amber-400 font-bold mb-1">3. Marketing Broadcasts (Promos & Restocks)</div>
                                        <p className="text-xs text-zinc-300 leading-relaxed">
                                            Used for holiday sales, new product drops, and abandoned cart recovery to opt-in Nigerian customer lists. Includes interactive Call-to-Action and Quick Reply buttons.
                                        </p>
                                    </div>
                                    <div className="p-5 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                        <div className="text-xs font-mono text-purple-400 font-bold mb-1">4. Authentication (OTP Verification)</div>
                                        <p className="text-xs text-zinc-300 leading-relaxed">
                                            Replaces unreliable Nigerian SMS OTPs that get blocked by MTN/Airtel DND filters with instant, encrypted WhatsApp one-time passcodes.
                                        </p>
                                    </div>
                                </div>
                            </section>

                            <section id="platform-comparison" className="space-y-4">
                                <h2 className="text-2xl font-bold text-white">
                                    3. Bytecohort Reach vs. Wati vs. Twilio in Nigeria (2026 Comparison)
                                </h2>
                                <div className="overflow-x-auto rounded-xl border border-white/[0.08] bg-[#0B0D14]">
                                    <table className="w-full text-left border-collapse text-xs">
                                        <thead>
                                            <tr className="border-b border-white/[0.08] bg-zinc-900/60 text-zinc-300 font-mono">
                                                <th className="p-4">Capability / Cost Factor</th>
                                                <th className="p-4 text-emerald-400 font-bold">Bytecohort Reach 🇳🇬</th>
                                                <th className="p-4">Wati / Respond.io</th>
                                                <th className="p-4">Twilio API</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/[0.06] text-zinc-300">
                                            <tr>
                                                <td className="p-4 font-medium text-white">Billing Currency</td>
                                                <td className="p-4 text-emerald-400 font-semibold">Nigerian Naira (₦) via Paystack</td>
                                                <td className="p-4 text-zinc-400">USD Only ($49–$119+/mo)</td>
                                                <td className="p-4 text-zinc-400">USD Only (International Card)</td>
                                            </tr>
                                            <tr>
                                                <td className="p-4 font-medium text-white">Per-Message Broker Markup</td>
                                                <td className="p-4 text-emerald-400 font-semibold">0% (Direct Meta Cloud API)</td>
                                                <td className="p-4 text-zinc-400">15% – 20% markup per message</td>
                                                <td className="p-4 text-zinc-400">$0.005 USD fee per message</td>
                                            </tr>
                                            <tr>
                                                <td className="p-4 font-medium text-white">2-Way Google Sheets Sync</td>
                                                <td className="p-4 text-emerald-400 font-semibold">Built-In Native (Real-Time)</td>
                                                <td className="p-4 text-zinc-400">Requires paid Zapier/Make subscription</td>
                                                <td className="p-4 text-zinc-400">Requires custom Node/Python coding</td>
                                            </tr>
                                            <tr>
                                                <td className="p-4 font-medium text-white">Visual Gemini AI Agent Builder</td>
                                                <td className="p-4 text-emerald-400 font-semibold">Included (No-Code Flow + Knowledge Base)</td>
                                                <td className="p-4 text-zinc-400">Paid add-on / basic keyword rules</td>
                                                <td className="p-4 text-zinc-400">Must build from scratch</td>
                                            </tr>
                                            <tr>
                                                <td className="p-4 font-medium text-white">+234 Number Normalization</td>
                                                <td className="p-4 text-emerald-400 font-semibold">Automatic (080, 081, 090, 070 → +234)</td>
                                                <td className="p-4 text-zinc-400">Manual CSV formatting required</td>
                                                <td className="p-4 text-zinc-400">Rejects non-E.164 numbers with error</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </section>

                            <section id="avoid-fx-traps" className="space-y-3">
                                <h2 className="text-2xl font-bold text-white">
                                    4. Eliminating USD Virtual Card Failures & FX Volatility
                                </h2>
                                <p className="text-sm text-zinc-300 leading-relaxed">
                                    One of the biggest operational risks for Nigerian e-commerce brands and fintechs is having their customer support or campaign tool suspended mid-month because a virtual USD card provider declined an automated recurring charge. By subscribing in Naira through Paystack on <Link href="/pricing" className="text-emerald-400 underline">Bytecohort Reach Naira Plans</Link>, your finance team gets predictable NGN invoices without FX surprises.
                                </p>
                            </section>
                        </>
                    )}

                    {/* PLAYBOOK 2 SPECIFIC CONTENT: WASENDER BAN & OFFICIAL MIGRATION */}
                    {slug === 'wasender-gbwhatsapp-ban-official-meta-api-nigeria' && (
                        <>
                            <section id="ban-risk-checker" className="p-6 sm:p-8 rounded-2xl bg-[#0B0D14] border border-amber-500/30 shadow-xl">
                                <div className="flex items-center gap-2 text-amber-400 text-xs font-mono uppercase tracking-wider mb-2">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span>Interactive +234 Number Safety Diagnostic</span>
                                </div>
                                <h2 className="text-2xl font-bold text-white mb-2">
                                    1. Check Your Nigerian WhatsApp Number Ban Risk
                                </h2>
                                <p className="text-xs sm:text-sm text-zinc-400 mb-6">
                                    Select how your team currently sends WhatsApp broadcasts in Nigeria to calculate your account suspension probability under Meta’s 2026 anti-spam enforcement:
                                </p>

                                <div className="space-y-3 mb-6">
                                    <label className="flex items-center justify-between p-4 rounded-xl bg-zinc-900/70 border border-white/[0.08] cursor-pointer">
                                        <div>
                                            <div className="text-xs sm:text-sm font-semibold text-white">Using Wasender, Chrome Extensions, or Modified APKs (GBWhatsApp)</div>
                                            <div className="text-[11px] text-zinc-400">Tools that scan a QR code and automate clicks inside WhatsApp Web</div>
                                        </div>
                                        <input
                                            type="checkbox"
                                            checked={usesBrowserExtension}
                                            onChange={(e) => setUsesBrowserExtension(e.target.checked)}
                                            className="w-5 h-5 accent-emerald-400"
                                        />
                                    </label>
                                    <label className="flex items-center justify-between p-4 rounded-xl bg-zinc-900/70 border border-white/[0.08] cursor-pointer">
                                        <div>
                                            <div className="text-xs sm:text-sm font-semibold text-white">Sending Bulk Messages Without Approved Meta Templates</div>
                                            <div className="text-[11px] text-zinc-400">Blasting raw promotional text to numbers that have not messaged you in 24h</div>
                                        </div>
                                        <input
                                            type="checkbox"
                                            checked={sendsColdUnapproved}
                                            onChange={(e) => setSendsColdUnapproved(e.target.checked)}
                                            className="w-5 h-5 accent-emerald-400"
                                        />
                                    </label>
                                    <label className="flex items-center justify-between p-4 rounded-xl bg-zinc-900/70 border border-white/[0.08] cursor-pointer">
                                        <div>
                                            <div className="text-xs sm:text-sm font-semibold text-white">No Interactive Opt-Out / Quick Reply Buttons</div>
                                            <div className="text-[11px] text-zinc-400">Forcing annoyed recipients to click "Report & Block" instead of a clean button</div>
                                        </div>
                                        <input
                                            type="checkbox"
                                            checked={noOptOutButton}
                                            onChange={(e) => setNoOptOutButton(e.target.checked)}
                                            className="w-5 h-5 accent-emerald-400"
                                        />
                                    </label>
                                </div>

                                <div className="p-5 rounded-xl bg-zinc-950 border border-white/[0.08] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                    <div>
                                        <div className="text-xs font-mono uppercase text-zinc-400">Calculated +234 Ban Probability</div>
                                        <div className={`text-3xl font-black font-mono mt-1 ${banRiskScore >= 50 ? 'text-red-400' : banRiskScore > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                                            {banRiskScore}% Risk ({banRiskScore >= 50 ? 'CRITICAL DANGER' : banRiskScore > 0 ? 'MODERATE RISK' : '100% COMPLIANT'})
                                        </div>
                                    </div>
                                    <Link
                                        href="/app"
                                        className="px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-xs inline-flex items-center gap-2 shrink-0"
                                    >
                                        <ShieldCheck className="w-4 h-4" />
                                        <span>Switch to Official Meta API</span>
                                    </Link>
                                </div>
                            </section>

                            <section id="how-meta-detects" className="space-y-4">
                                <h2 className="text-2xl font-bold text-white">
                                    2. How Meta Detects Wasender, Chrome Extensions & Modified APKs
                                </h2>
                                <p className="text-sm text-zinc-300 leading-relaxed">
                                    Many Nigerian businesses fall for cheap "lifetime license" bulk WhatsApp scripts sold on social media. Under the hood, those tools inject JavaScript into WhatsApp Web’s DOM to open chats and paste messages every few seconds. Meta’s security engine detects this through three signals:
                                </p>
                                <ul className="space-y-3 text-xs sm:text-sm text-zinc-300">
                                    <li className="p-4 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                        <strong className="text-white">1. Headless Chromium & DOM Injection Signatures:</strong> WhatsApp Web checks whether messages are triggered by genuine physical keystrokes/pointer events or injected via scripted DOM mutations.
                                    </li>
                                    <li className="p-4 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                        <strong className="text-white">2. Outbound New-Thread Velocity:</strong> When a consumer or regular WhatsApp Business App number initiates dozens of outbound threads to unsaved +234 numbers in minutes, Meta automatically freezes the SIM.
                                    </li>
                                    <li className="p-4 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                        <strong className="text-white">3. Missing Template Pre-Approval:</strong> Only the official Meta Graph API (`graph.facebook.com/v21.0`) is authorized to send high-volume business-initiated templates.
                                    </li>
                                </ul>
                            </section>

                            <section id="architecture-diff" className="space-y-4">
                                <h2 className="text-2xl font-bold text-white">
                                    3. Unofficial DOM Scrapers vs. Official Meta Cloud API
                                </h2>
                                <p className="text-sm text-zinc-300 leading-relaxed">
                                    When you connect your Nigerian business to <strong>Bytecohort Reach</strong>, your messages never pass through a fragile browser tab. Instead, they dispatch over encrypted HTTPS webhooks directly to Meta’s official Cloud API infrastructure—giving you 99.8% delivery across MTN, Airtel, Glo, and 9mobile, even when your laptop and phone are turned off.
                                </p>
                            </section>

                            <section id="migration-steps" className="space-y-4">
                                <h2 className="text-2xl font-bold text-white">
                                    4. Step-by-Step: Migrating Your +234 Number in 5 Minutes
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="p-5 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                        <div className="text-emerald-400 font-mono font-bold text-xs mb-2">STEP 01</div>
                                        <h3 className="text-sm font-bold text-white mb-1">Click Meta Embedded Signup</h3>
                                        <p className="text-xs text-zinc-400">Inside Bytecohort Reach, click "Connect with Facebook" to launch the official Meta popup.</p>
                                    </div>
                                    <div className="p-5 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                        <div className="text-emerald-400 font-mono font-bold text-xs mb-2">STEP 02</div>
                                        <h3 className="text-sm font-bold text-white mb-1">Verify Your +234 Number</h3>
                                        <p className="text-xs text-zinc-400">Receive a 6-digit SMS or voice call OTP on your Nigerian SIM to register it on the Cloud API.</p>
                                    </div>
                                    <div className="p-5 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                        <div className="text-emerald-400 font-mono font-bold text-xs mb-2">STEP 03</div>
                                        <h3 className="text-sm font-bold text-white mb-1">Launch Compliant Campaigns</h3>
                                        <p className="text-xs text-zinc-400">Sync your Google Sheet and let our Gemini AI template generator create high-converting approved templates.</p>
                                    </div>
                                </div>
                            </section>
                        </>
                    )}

                    {/* PLAYBOOK 3 SPECIFIC CONTENT: PAYSTACK + GOOGLE SHEETS ORDER AUTOMATION */}
                    {slug === 'paystack-whatsapp-order-automation-google-sheets-nigeria' && (
                        <>
                            <section id="copyable-templates" className="p-6 sm:p-8 rounded-2xl bg-[#0B0D14] border border-emerald-500/30 space-y-6">
                                <div>
                                    <div className="flex items-center gap-2 text-emerald-400 text-xs font-mono uppercase tracking-wider mb-2">
                                        <Terminal className="w-4 h-4" />
                                        <span>Production-Ready Nigerian Templates</span>
                                    </div>
                                    <h2 className="text-2xl font-bold text-white mb-2">
                                        1. Copyable WhatsApp Order Template & Gemini AI Nigerian Sales Prompt
                                    </h2>
                                    <p className="text-xs sm:text-sm text-zinc-400">
                                        Copy these battle-tested templates directly into your Bytecohort Reach workspace to automate order confirmations and 24/7 AI customer triage.
                                    </p>
                                </div>

                                {/* Snippet 1 */}
                                <div className="rounded-xl bg-zinc-950 border border-white/[0.08] overflow-hidden">
                                    <div className="px-4 py-2.5 bg-zinc-900/80 border-b border-white/[0.06] flex items-center justify-between">
                                        <span className="text-xs font-mono text-emerald-400">Meta Utility Template: Naira Order & Delivery Confirmation</span>
                                        <button
                                            onClick={() => handleCopy('template', COPY_SNIPPETS.metaTemplate)}
                                            className="inline-flex items-center gap-1.5 text-xs font-mono text-zinc-300 hover:text-white px-2.5 py-1 rounded bg-white/5"
                                        >
                                            {copiedKey === 'template' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                            <span>{copiedKey === 'template' ? 'Copied!' : 'Copy Template'}</span>
                                        </button>
                                    </div>
                                    <pre className="p-4 text-xs text-zinc-300 font-mono whitespace-pre-wrap leading-relaxed">
                                        {COPY_SNIPPETS.metaTemplate}
                                    </pre>
                                </div>

                                {/* Snippet 2 */}
                                <div className="rounded-xl bg-zinc-950 border border-white/[0.08] overflow-hidden">
                                    <div className="px-4 py-2.5 bg-zinc-900/80 border-b border-white/[0.06] flex items-center justify-between">
                                        <span className="text-xs font-mono text-cyan-400">Gemini AI Agent System Prompt: Nigerian E-Commerce & Logistics</span>
                                        <button
                                            onClick={() => handleCopy('prompt', COPY_SNIPPETS.geminiPrompt)}
                                            className="inline-flex items-center gap-1.5 text-xs font-mono text-zinc-300 hover:text-white px-2.5 py-1 rounded bg-white/5"
                                        >
                                            {copiedKey === 'prompt' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                            <span>{copiedKey === 'prompt' ? 'Copied!' : 'Copy AI Prompt'}</span>
                                        </button>
                                    </div>
                                    <pre className="p-4 text-xs text-zinc-300 font-mono whitespace-pre-wrap leading-relaxed">
                                        {COPY_SNIPPETS.geminiPrompt}
                                    </pre>
                                </div>
                            </section>

                            <section id="sheets-pipeline" className="space-y-4">
                                <h2 className="text-2xl font-bold text-white">
                                    2. Designing Your Google Sheets Order & Logistics Pipeline
                                </h2>
                                <p className="text-sm text-zinc-300 leading-relaxed">
                                    Across Lagos, Abuja, and Port Harcourt, over 80% of fast-growing merchants and logistics operators manage daily orders inside Google Sheets. Instead of forcing your staff to learn a complicated foreign CRM, Bytecohort Reach turns your existing spreadsheet into an automated WhatsApp command center:
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="p-4 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                        <Database className="w-5 h-5 text-emerald-400 mb-2" />
                                        <h3 className="text-sm font-bold text-white mb-1">1. Instant Row Trigger</h3>
                                        <p className="text-xs text-zinc-400">When Paystack or your staff adds a row to Google Sheets, Reach detects it automatically.</p>
                                    </div>
                                    <div className="p-4 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                        <Zap className="w-5 h-5 text-cyan-400 mb-2" />
                                        <h3 className="text-sm font-bold text-white mb-1">2. Dynamic Variable Merge</h3>
                                        <p className="text-xs text-zinc-400">Merges Customer Name, Order ID, Amount (₦), and Dispatch Rider Phone into your Meta template.</p>
                                    </div>
                                    <div className="p-4 rounded-xl bg-[#0B0D14] border border-white/[0.08]">
                                        <Bot className="w-5 h-5 text-purple-400 mb-2" />
                                        <h3 className="text-sm font-bold text-white mb-1">3. Live 2-Way Lookup</h3>
                                        <p className="text-xs text-zinc-400">When the buyer replies on WhatsApp, Gemini AI queries the sheet in 280ms to answer status questions.</p>
                                    </div>
                                </div>
                            </section>

                            <section id="number-normalization" className="space-y-3">
                                <h2 className="text-2xl font-bold text-white">
                                    3. Automatic +234 Phone Number Normalization (080, 081, 090, 070)
                                </h2>
                                <p className="text-sm text-zinc-300 leading-relaxed">
                                    Nigerian customers rarely type `+234` into checkout forms—they enter `0803...`, `0816...`, or `0905...`. Standard foreign tools reject these numbers with formatting errors. Bytecohort Reach’s built-in sanitizer automatically transforms local 11-digit Nigerian numbers into compliant E.164 international format (`+234803...`) before calling the Meta Cloud API.
                                </p>
                            </section>

                            <section id="ai-handoff" className="space-y-3">
                                <h2 className="text-2xl font-bold text-white">
                                    4. Training Gemini AI for Lagos/Abuja Delivery & Human Handoff
                                </h2>
                                <p className="text-sm text-zinc-300 leading-relaxed">
                                    With Bytecohort Reach’s visual Agent Builder, your AI handles repetitive inquiries ("How much is delivery to Lekki Phase 1?", "Do you have size 42 in stock?") 24/7, and seamlessly transfers complex negotiations to your human sales reps in the Unified Team Inbox.
                                </p>
                            </section>
                        </>
                    )}

                    {/* FAQ Section for All Playbooks */}
                    <section id="faq" className="pt-8 border-t border-white/[0.08] space-y-6">
                        <div className="flex items-center gap-2 text-emerald-400 text-xs font-mono uppercase tracking-wider">
                            <HelpCircle className="w-4 h-4" />
                            <span>Frequently Asked Questions</span>
                        </div>
                        <h2 className="text-2xl font-bold text-white">
                            Common Questions & Technical Answers
                        </h2>
                        <div className="space-y-3">
                            {config.faqs.map((faq, idx) => {
                                const isOpen = openFaq === idx;
                                return (
                                    <div key={idx} className="rounded-xl border border-white/[0.08] bg-[#0B0D14] overflow-hidden">
                                        <button
                                            onClick={() => setOpenFaq(isOpen ? null : idx)}
                                            className="w-full p-5 text-left flex items-center justify-between gap-4"
                                        >
                                            <span className="text-sm sm:text-base font-semibold text-white">{faq.question}</span>
                                            {isOpen ? <ChevronUp className="w-4 h-4 text-emerald-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" />}
                                        </button>
                                        {isOpen && (
                                            <div className="px-5 pb-5 pt-1 text-xs sm:text-sm text-zinc-300 leading-relaxed border-t border-white/[0.04]">
                                                {faq.answer}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* Related Nigeria Playbooks (Internal Linking Mesh) */}
                    <section className="pt-8 border-t border-white/[0.08] space-y-4">
                        <h3 className="text-lg font-bold text-white">
                            Continue Reading: Related Nigeria WhatsApp Automation Playbooks
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {(Object.values(PLAYBOOKS) as PlaybookConfig[])
                                .filter((p) => p.slug !== slug)
                                .map((rel) => (
                                    <Link
                                        key={rel.slug}
                                        href={`/blog/${rel.slug}`}
                                        className="p-5 rounded-xl bg-[#0B0D14] border border-white/[0.08] hover:border-emerald-500/40 transition-all block group"
                                    >
                                        <span className="text-[10px] font-mono text-emerald-400 uppercase">{rel.category}</span>
                                        <h4 className="text-sm font-bold text-white group-hover:text-emerald-300 mt-1 mb-2 leading-snug">
                                            {rel.title}
                                        </h4>
                                        <span className="text-xs text-zinc-400 group-hover:text-white inline-flex items-center gap-1">
                                            <span>Read Playbook</span>
                                            <ArrowRight className="w-3 h-3" />
                                        </span>
                                    </Link>
                                ))}
                        </div>
                    </section>

                    {/* Bottom Conversion Banner */}
                    <section className="p-8 rounded-2xl bg-gradient-to-r from-emerald-950/40 via-[#0B0D14] to-[#0B0D14] border border-emerald-500/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                        <div className="max-w-xl">
                            <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                                Ready to launch compliant WhatsApp automation in Nigeria?
                            </h3>
                            <p className="text-xs sm:text-sm text-zinc-300">
                                Connect your +234 number to the official Meta Cloud API, sync Google Sheets, and deploy a 24/7 Gemini AI agent today. Pay seamlessly in Naira (₦).
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
                            <button
                                onClick={() => navigate('/app')}
                                className="px-6 py-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-xs inline-flex items-center justify-center gap-2"
                            >
                                <span>Start Free Trial</span>
                                <ArrowRight className="w-4 h-4" />
                            </button>
                            <Link
                                href="/pricing"
                                className="px-5 py-3.5 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium text-xs border border-white/10 inline-flex items-center justify-center gap-2"
                            >
                                <CreditCard className="w-4 h-4 text-emerald-400" />
                                <span>Naira Plans</span>
                            </Link>
                        </div>
                    </section>
                </article>
            </main>

            {/* Footer */}
            <footer className="border-t border-white/[0.08] py-10 px-4 sm:px-6 text-xs text-zinc-500">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p>© {new Date().getFullYear()} Bytecohort Reach. Official Meta WhatsApp Cloud API Partner in Nigeria.</p>
                    <div className="flex flex-wrap items-center gap-5">
                        <Link href="/whatsapp-automation-nigeria" className="text-emerald-400 hover:underline">WhatsApp Automation Nigeria</Link>
                        <Link href="/blog" className="hover:text-white">All Playbooks</Link>
                        <Link href="/pricing" className="hover:text-white">Pricing (NGN)</Link>
                        <Link href="/docs" className="hover:text-white">API Docs</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default NigeriaPlaybookPage;
